import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  BookOpen,
  CreditCard,
  TrendingUp,
  TrendingDown
} from 'lucide-react'
// import { accountAPI } from '../services/api' // Removed API calls
import { LoadingSpinner } from '../components'
import toast from 'react-hot-toast'

function Accounts() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [editingAccount, setEditingAccount] = useState(null)

  const queryClient = useQueryClient()

  // Fetch accounts
  const { data: accountsData, isLoading } = useQuery(
    ['accounts', { page: currentPage, search: searchTerm, accountType: selectedType }],
    () => accountAPI.getAll({
      page: currentPage,
      limit: 10,
      search: searchTerm,
      accountType: selectedType,
    })
  )

  // Create account mutation
  const createAccountMutation = useMutation(accountAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('accounts')
      setShowModal(false)
      toast.success('Account created successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create account')
    },
  })

  // Update account mutation
  const updateAccountMutation = useMutation(
    ({ id, data }) => accountAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('accounts')
        setShowModal(false)
        setEditingAccount(null)
        toast.success('Account updated successfully!')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update account')
      },
    }
  )

  // Delete account mutation
  const deleteAccountMutation = useMutation(accountAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('accounts')
      toast.success('Account deleted successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete account')
    },
  })

  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  const onSubmit = (data) => {
    if (editingAccount) {
      updateAccountMutation.mutate({ id: editingAccount.id, data })
    } else {
      createAccountMutation.mutate(data)
    }
  }

  const handleEdit = (account) => {
    setEditingAccount(account)
    reset({
      accountName: account.accountName,
      accountType: account.accountType,
      openingBalance: account.openingBalance,
      currentBalance: account.currentBalance,
      description: account.description,
    })
    setShowModal(true)
  }

  const handleDelete = (account) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      deleteAccountMutation.mutate(account.id)
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingAccount(null)
    reset()
  }

  const getAccountTypeIcon = (type) => {
    switch (type) {
      case 'Asset':
        return <TrendingUp className="h-4 w-4 text-green-600" />
      case 'Liability':
        return <TrendingDown className="h-4 w-4 text-red-600" />
      case 'Equity':
        return <CreditCard className="h-4 w-4 text-blue-600" />
      case 'Revenue':
        return <TrendingUp className="h-4 w-4 text-green-600" />
      case 'Expense':
        return <TrendingDown className="h-4 w-4 text-red-600" />
      default:
        return <BookOpen className="h-4 w-4 text-gray-600" />
    }
  }

  const getAccountTypeColor = (type) => {
    switch (type) {
      case 'Asset':
        return 'badge-success'
      case 'Liability':
        return 'badge-danger'
      case 'Equity':
        return 'badge-secondary'
      case 'Revenue':
        return 'badge-success'
      case 'Expense':
        return 'badge-danger'
      default:
        return 'badge-secondary'
    }
  }

  const accounts = accountsData?.data?.accounts || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-secondary-900">Accounts</h1>
          <p className="mt-1 text-base text-secondary-600">
            Manage chart of accounts and financial ledgers
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Account
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-content">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search accounts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            <div>
              <label className="label">Account Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="input"
              >
                <option value="">All Types</option>
                <option value="Asset">Asset</option>
                <option value="Liability">Liability</option>
                <option value="Equity">Equity</option>
                <option value="Revenue">Revenue</option>
                <option value="Expense">Expense</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedType('')
                }}
                className="btn-outline w-full"
              >
                <Filter className="h-4 w-4 mr-2" />
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Accounts Table */}
      <div className="card">
        <div className="card-content p-0">
          {isLoading ? (
            <LoadingSpinner size="lg" className="h-64" />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr className="table-row">
                    <th className="table-head">Account</th>
                    <th className="table-head">Type</th>
                    <th className="table-head">Opening Balance</th>
                    <th className="table-head">Current Balance</th>
                    <th className="table-head">Description</th>
                    <th className="table-head">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {accounts.map((account) => (
                    <tr key={account.id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <BookOpen className="h-4 w-4 text-primary-600" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-secondary-900">
                              {account.accountName}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          {getAccountTypeIcon(account.accountType)}
                          <span className={`ml-1 badge ${getAccountTypeColor(account.accountType)}`}>
                            {account.accountType}
                          </span>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center text-sm text-secondary-900">
                          <CreditCard className="h-4 w-4 mr-1" />
                          ₹{account.openingBalance}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center text-sm text-secondary-900">
                          <CreditCard className="h-4 w-4 mr-1" />
                          ₹{account.currentBalance}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-secondary-900">
                          {account.description || 'No description'}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(account)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(account)}
                            className="text-danger-600 hover:text-danger-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Account Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="modal-overlay" onClick={handleCloseModal} />
            <div className="modal-content max-w-2xl">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                {editingAccount ? 'Edit Account' : 'Add New Account'}
              </h3>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="label">Account Name</label>
                  <input
                    {...register('accountName', { required: 'Account name is required' })}
                    className="input"
                    placeholder="Enter account name"
                  />
                  {errors.accountName && (
                    <p className="form-error">{errors.accountName.message}</p>
                  )}
                </div>

                <div>
                  <label className="label">Account Type</label>
                  <select
                    {...register('accountType', { required: 'Account type is required' })}
                    className="input"
                  >
                    <option value="">Select account type</option>
                    <option value="Asset">Asset</option>
                    <option value="Liability">Liability</option>
                    <option value="Equity">Equity</option>
                    <option value="Revenue">Revenue</option>
                    <option value="Expense">Expense</option>
                  </select>
                  {errors.accountType && (
                    <p className="form-error">{errors.accountType.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Opening Balance</label>
                    <input
                      {...register('openingBalance')}
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="Enter opening balance"
                    />
                  </div>
                  <div>
                    <label className="label">Current Balance</label>
                    <input
                      {...register('currentBalance')}
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="Enter current balance"
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Description</label>
                  <textarea
                    {...register('description')}
                    className="input"
                    rows={3}
                    placeholder="Enter account description"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createAccountMutation.isLoading || updateAccountMutation.isLoading}
                    className="btn-primary"
                  >
                    {createAccountMutation.isLoading || updateAccountMutation.isLoading ? (
                      <LoadingSpinner size="sm" className="mr-2" />
                    ) : null}
                    {editingAccount ? 'Update Account' : 'Create Account'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Accounts
